import { useState, useEffect, useCallback, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/hooks/use-auth";
import { RefreshCw, Pause, Play, X, Loader2, Users, TrendingUp, AlertCircle } from "lucide-react";

interface SubscriptionRow {
  readonly id: string;
  readonly status: string;
  readonly billing_cycle: string;
  readonly price: number;
  readonly next_billing_date: string;
  readonly failed_attempts: number;
  readonly created_at: string;
  readonly product_name: string | null;
  readonly customer_name: string | null;
}

interface Stats {
  readonly activeCount: number;
  readonly pausedCount: number;
  readonly mrr: number;
}

type StatusFilter = "all" | "active" | "paused" | "cancelled";

const CYCLE_LABELS: Record<string, string> = {
  monthly:   "חודשי",
  quarterly: "רבעוני",
  yearly:    "שנתי",
};

const STATUS_MAP: Record<string, { label: string; className: string }> = {
  active:    { label: "פעיל",      className: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" },
  paused:    { label: "מושהה",     className: "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/30" },
  cancelled: { label: "בוטל",      className: "bg-slate-500/15 text-slate-500 border-slate-500/30" },
  expired:   { label: "פג תוקף",  className: "bg-destructive/15 text-destructive border-destructive/30" },
};

function formatPrice(agorot: number): string {
  return `₪${(agorot / 100).toFixed(2)}`;
}

function formatDate(iso: string): string {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("he-IL");
}

export function SubscriptionListPage(): ReactNode {
  const { accessToken } = useAuth();
  const [subs, setSubs] = useState<readonly SubscriptionRow[]>([]);
  const [stats, setStats] = useState<Stats>({ activeCount: 0, pausedCount: 0, mrr: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<StatusFilter>("all");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const [subsRes, statsRes] = await Promise.all([
        fetch(`/api/subscriptions/admin${params}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch("/api/subscriptions/admin/stats", {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
      ]);

      const subsJson = (await subsRes.json()) as { success: boolean; data: readonly SubscriptionRow[] };
      const statsJson = (await statsRes.json()) as { success: boolean; data: Stats };

      if (subsJson.success) setSubs(subsJson.data);
      if (statsJson.success) setStats(statsJson.data);
    } finally {
      setLoading(false);
    }
  }, [accessToken, filter]);

  useEffect(() => { void fetchData(); }, [fetchData]);

  const handleAction = useCallback(
    async (id: string, action: "pause" | "resume" | "cancel") => {
      await fetch(`/api/subscriptions/admin/${id}/${action}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      void fetchData();
    },
    [accessToken, fetchData],
  );

  const filters: readonly { value: StatusFilter; label: string }[] = [
    { value: "all",       label: "הכל" },
    { value: "active",    label: `פעילות (${stats.activeCount})` },
    { value: "paused",    label: `מושהות (${stats.pausedCount})` },
    { value: "cancelled", label: "מבוטלות" },
  ];

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">סבסקריפציות</h1>
          <p className="text-sm text-muted-foreground mt-0.5">ניהול מנויים חוזרים</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void fetchData()}
          className="min-h-[38px]"
          title="רענן"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="shadow-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
              <Users className="h-5 w-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">פעילות</p>
              <p className="text-2xl font-bold">{stats.activeCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <TrendingUp className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">MRR</p>
              <p className="text-2xl font-bold">{formatPrice(stats.mrr)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="h-10 w-10 rounded-xl bg-yellow-500/10 flex items-center justify-center shrink-0">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">מושהות</p>
              <p className={cn("text-2xl font-bold", stats.pausedCount > 0 && "text-yellow-600 dark:text-yellow-400")}>
                {stats.pausedCount}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {filters.map((f) => (
          <Button
            key={f.value}
            variant={filter === f.value ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f.value)}
            className="min-h-[38px] whitespace-nowrap"
          >
            {f.label}
          </Button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : subs.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">אין סבסקריפציות</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rounded-xl border bg-card shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="p-3 text-start text-xs font-semibold text-muted-foreground uppercase tracking-wide">לקוח</th>
                  <th className="p-3 text-start text-xs font-semibold text-muted-foreground uppercase tracking-wide">מוצר</th>
                  <th className="p-3 text-start text-xs font-semibold text-muted-foreground uppercase tracking-wide">מחזור</th>
                  <th className="p-3 text-start text-xs font-semibold text-muted-foreground uppercase tracking-wide">מחיר</th>
                  <th className="p-3 text-start text-xs font-semibold text-muted-foreground uppercase tracking-wide">חיוב הבא</th>
                  <th className="p-3 text-start text-xs font-semibold text-muted-foreground uppercase tracking-wide">סטטוס</th>
                  <th className="p-3 text-end text-xs font-semibold text-muted-foreground uppercase tracking-wide">פעולות</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {subs.map((sub) => {
                  const st = STATUS_MAP[sub.status] ?? STATUS_MAP["cancelled"]!;
                  return (
                    <tr key={sub.id} className="hover:bg-muted/30 transition-colors">
                      <td className="p-3 font-medium">{sub.customer_name ?? "-"}</td>
                      <td className="p-3 text-muted-foreground text-xs">{sub.product_name ?? "-"}</td>
                      <td className="p-3 text-xs">{CYCLE_LABELS[sub.billing_cycle] ?? sub.billing_cycle}</td>
                      <td className="p-3 font-mono font-semibold">{formatPrice(sub.price)}</td>
                      <td className="p-3 text-xs text-muted-foreground">{formatDate(sub.next_billing_date)}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold", st.className)}>
                            {st.label}
                          </span>
                          {sub.failed_attempts > 0 && (
                            <span className="text-xs text-destructive font-medium">
                              {sub.failed_attempts} כשלונות
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-end">
                        <div className="flex gap-1 justify-end">
                          {sub.status === "active" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="min-h-[36px] min-w-[36px] p-0"
                              onClick={() => void handleAction(sub.id, "pause")}
                              title="השהה"
                            >
                              <Pause className="h-4 w-4" />
                            </Button>
                          )}
                          {sub.status === "paused" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="min-h-[36px] min-w-[36px] p-0"
                              onClick={() => void handleAction(sub.id, "resume")}
                              title="חידוש"
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                          )}
                          {(sub.status === "active" || sub.status === "paused") && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="min-h-[36px] min-w-[36px] p-0 text-destructive hover:text-destructive"
                              onClick={() => void handleAction(sub.id, "cancel")}
                              title="ביטול"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {subs.map((sub) => {
              const st = STATUS_MAP[sub.status] ?? STATUS_MAP["cancelled"]!;
              return (
                <Card key={sub.id} className="shadow-sm">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold">{sub.customer_name ?? "-"}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{sub.product_name ?? "-"}</p>
                      </div>
                      <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold shrink-0", st.className)}>
                        {st.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-mono font-semibold">{formatPrice(sub.price)}</span>
                      <span className="text-xs text-muted-foreground">
                        {CYCLE_LABELS[sub.billing_cycle] ?? sub.billing_cycle} · {formatDate(sub.next_billing_date)}
                      </span>
                    </div>
                    {sub.failed_attempts > 0 && (
                      <p className="text-xs text-destructive font-medium">תשלום נכשל {sub.failed_attempts} פעמים</p>
                    )}
                    <div className="flex gap-2 justify-end pt-1">
                      {sub.status === "active" && (
                        <Button variant="outline" size="sm" className="min-h-[44px] gap-1" onClick={() => void handleAction(sub.id, "pause")}>
                          <Pause className="h-3.5 w-3.5" /> השהה
                        </Button>
                      )}
                      {sub.status === "paused" && (
                        <Button variant="outline" size="sm" className="min-h-[44px] gap-1" onClick={() => void handleAction(sub.id, "resume")}>
                          <Play className="h-3.5 w-3.5" /> חדש
                        </Button>
                      )}
                      {(sub.status === "active" || sub.status === "paused") && (
                        <Button variant="destructive" size="sm" className="min-h-[44px] gap-1" onClick={() => void handleAction(sub.id, "cancel")}>
                          <X className="h-3.5 w-3.5" /> בטל
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
